import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@journ/ui/alert-dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { Sheet, SheetContent, SheetHeader } from '@journ/ui/sheet'
import { useEffect } from 'react'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import { session$ } from '@/features/auth/store'
import { useMember } from '../hooks/use-member'
import { useMemberActions } from '../hooks/use-member-actions'
import { GLOBAL_SHEET_IDS, useSheet } from '@/features/ui/hooks/use-sheet'
import { refreshMembersList } from '../store'
import { MemberForm } from './member-form'
import type { FormValues } from './member-form'

const MemberEditSheet = () => {
	const content = useIntlayer('hr-page')
	const { isOpen, entityId: memberId, close } = useSheet(GLOBAL_SHEET_IDS.MEMBER_EDIT)
	const { member } = useMember(memberId ?? null)
	const { saveMember, archiveTeamMember } = useMemberActions()
	const user = useSelector(() => session$.user.get())
	const canManage = usePermission('hr.members.manage')
	const canEditOwn = usePermission('hr.members.edit-own')

	const canEditMember = canManage || (canEditOwn && user?.email === member?.email)

	useEffect(() => {
		if (!isOpen || !member || canEditMember) return
		close()
	}, [isOpen, member, canEditMember, close])

	const handleSubmit = async (values: FormValues): Promise<void> => {
		if (!canEditMember) return
		await saveMember({
			id: memberId!,
			fullName: values.fullName,
			email: values.email,
			employmentType: values.employmentType ?? member?.employmentType ?? 'full_time',
			status: values.status ?? member?.status ?? 'active',
			preferredName: values.preferredName ?? null,
			personalEmail: values.personalEmail ?? null,
			workPhone: values.workPhone ?? null,
			personalPhone: values.personalPhone ?? null,
			position: values.position ?? null,
			departmentId: values.departmentId ?? null,
			startDate: values.startDate ?? null,
			location: values.location ?? null,
			timezone: values.timezone ?? null,
			addressLine1: values.addressLine1 ?? null,
			addressLine2: values.addressLine2 ?? null,
			city: values.city ?? null,
			state: values.state ?? null,
			countryCode: values.countryCode ?? null,
			zip: values.zip ?? null,
			defaultWorkSchedule: values.defaultWorkSchedule ?? null,
		})
		if (user && memberId === user.id) {
			session$.user.set({ ...user, fullName: values.fullName, email: values.email })
		}
		refreshMembersList()
		close()
	}

	const handleArchive = async (): Promise<void> => {
		if (!memberId || !canManage) return
		await archiveTeamMember(memberId)
		refreshMembersList()
		close()
	}

	return (
		<Sheet open={isOpen} onOpenChange={close}>
			<SheetContent stack title={content.editSheet.title.value}>
				<SheetHeader className='mb-6 flex justify-between items-center flex-row'>
					<h2 className='text-xl'>{content.editSheet.title}</h2>
					{memberId && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<button type='button'>
									<Icons.MoreVertical className='size-5' />
								</button>
							</DropdownMenuTrigger>
							<DropdownMenuContent sideOffset={10} align='end'>
								<AlertDialog>
									<AlertDialogTrigger asChild>
										<DropdownMenuItem className='text-destructive' onSelect={e => e.preventDefault()}>
											{content.archiveDialog.confirm}
										</DropdownMenuItem>
									</AlertDialogTrigger>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>{content.archiveDialog.title}</AlertDialogTitle>
											<AlertDialogDescription>{content.archiveDialog.description}</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel>{content.archiveDialog.cancel}</AlertDialogCancel>
											<AlertDialogAction onClick={handleArchive}>{content.archiveDialog.confirm}</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</DropdownMenuContent>
						</DropdownMenu>
					)}
				</SheetHeader>
				{member && canEditMember && (
					<MemberForm data={member} key={member.id} onSubmit={handleSubmit} onCancel={close} />
				)}
			</SheetContent>
		</Sheet>
	)
}

export { MemberEditSheet }
